import { Component, Inject, Input, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Chart, ChartConfiguration } from 'chart.js';
import { MatrixController, MatrixElement } from 'chartjs-chart-matrix';
import { ClusterEntry } from '../../../../models/LocationClustersDTO';

Chart.register(MatrixController, MatrixElement);

export interface ClusterDetailsData {
  clusterId: number;
  entries: ClusterEntry[];
  color: string;
  percentage: number;
  identifier: string;
  description: string;
}

@Component({
  selector: 'app-recurrent-places-cluster-details',
  templateUrl: './recurrent-places-cluster-details.component.html',
  styleUrls: ['./recurrent-places-cluster-details.component.css']
})
export class RecurrentPlacesClusterDetailsComponent implements OnInit {

  private readonly dias  = ['L','M','MI','J','V','S','D'];
  private readonly horas = Array.from({length: 24}, (_, i) => `${String(i).padStart(2,'0')}:00`);

  daysMap: Map<string, number> = new Map([["Lunes", 0], ["Martes", 1], ["Miércoles", 2], ["Jueves", 3], ["Viernes", 4], ["Sábado", 5], ["Domingo", 6]]);
  daysCounter: Map<string, number> = new Map([["Lunes", 0], ["Martes", 0], ["Miércoles", 0], ["Jueves", 0], ["Viernes", 0], ["Sábado", 0], ["Domingo", 0]]);
  mostSignificantDay: string = "";
  mostSignificantHour: number = 0;

  baseColor: string = '#0093c0';
  dayNames: string[] = [];
  hourOfDay: number[] = [];
  totalEntryPoints: number = 0;
  entryPointsBreaks = [0, 1, 20, 50];

  public heatMapData: ChartConfiguration<'matrix'>['data'] = { datasets: [] };
  public heatMapOptions!: ChartConfiguration<'matrix'>['options'];



  


  constructor(
    public dialogRef: MatDialogRef<RecurrentPlacesClusterDetailsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ClusterDetailsData
  ) {
    this.dayNames = data.entries.map(e => e.day);
    this.hourOfDay = data.entries.map(e => e.hour);
    this.baseColor = data.color;
    this.totalEntryPoints = data.entries.reduce((sum, entry) => sum + entry.points, 0);
    for (const entry of data.entries) {
      this.daysCounter.set(entry.day, (this.daysCounter.get(entry.day) || 0) + entry.points);

      if(this.daysCounter.get(entry.day) > (this.daysCounter.get(this.mostSignificantDay) || 0)){
        this.mostSignificantDay = entry.day;
        this.mostSignificantHour = entry.hour;
      }
    }

    this.entryPointsBreaks = [0, 1, this.totalEntryPoints * 0.2, this.totalEntryPoints * 0.5];
  }

  ngOnInit(): void {
    this.initializeChart();
  }

  private generarDatos() {
    const data: { x: string; y: string; v: number }[] = [];

    for (const dia of this.daysMap.keys()) {
      for (const hora of this.horas) {
        const entryPoints = this.data.entries.find(e => e.day === dia && `${String(e.hour).padStart(2,'0')}:00` === hora)?.points || 0;
        data.push({
          x: this.dias[this.daysMap.get(dia) || 0],
          y: hora,
          v: entryPoints
        });
      }
    }
    return data;
  }

  initializeChart() {
    const ctx = document.getElementById('matrixChart') as HTMLCanvasElement;

    new Chart(ctx, {
      type: 'matrix',
      data: {
        datasets: [{
          label: 'Actividad por hora',
          data: this.generarDatos(),
          backgroundColor: (context) => {
            const value = (context.dataset.data[context.dataIndex] as any)?.v ?? 0;
            if (value === 0) return this.baseColor + '20';
            if (value <= this.entryPointsBreaks[2]) return this.baseColor + '40';
            if (value <= this.entryPointsBreaks[3]) return this.baseColor + '80';
            return this.baseColor;
          },
          borderWidth: 0.5,
          width: ({ chart }) => {
            if (!chart.chartArea) return 0;
            return (chart.chartArea.width / this.dias.length) - 2;
          },
          height: ({ chart }) => {
            if (!chart.chartArea) return 0;
            return (chart.chartArea.height / this.horas.length) - 1;
          }
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          datalabels: {
            display: false 
          },
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (items) => {
                const d = items[0].dataset.data[items[0].dataIndex] as any;
                return `${d.x} — ${d.y}`;
              },
              label: (item) => {
                const v = (item.dataset.data[item.dataIndex] as any).v;
                return `Actividad: ${v}%`;
              }
            }
          }
        },
        scales: {
          x: {
            type: 'category',
            labels: this.dias,
            offset: true,
            position: 'top',
            grid: { display: false }
          },
          y: {
            type: 'category',
            labels: this.horas,
            offset: true,
            reverse: true,
            grid: { display: false },
            ticks: {
              font: { size: 10 },
              callback(val, idx) {
                return idx % 3 === 0 ? this.getLabelForValue(val as number) : '';
              }
            }
          }
        }
      }
    });
  }



  close(): void {
    this.dialogRef.close();
  }

  
}
